import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import CopyToClipboard from 'react-copy-to-clipboard';
import type { CommonHTMLButtonAttributes } from '@frontend/types';
import { useThemeValues } from '../../hooks';
import { CheckIconSmall, CopyIcon } from '../../icon';
import { Button } from '../button';
import { Text } from '../text';
import { useTooltip } from '../tooltip';
import { CopyToClipboardStyles } from './copyable-text.styles';

type Props = {
  children?: React.ReactNode;
  hasIcon?: boolean;
  hasSuccessTooltip?: boolean;
  textToCopy: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
  tooltipText?: string;
  tooltipSuccessText?: string;
  icon?: typeof CopyIcon;
  trackingId?: string;
  disabled?: boolean;
};

const getIconColor = (variant: Props['variant'], children: React.ReactNode) => {
  if (variant === 'primary') return 'white';
  if (variant === 'secondary') return 'default';
  if (variant === 'tertiary' && !!children) return 'primary';
  return 'default';
};

const getTextColor = (variant: Props['variant']) => {
  if (variant === 'primary') return 'white';
  if (variant === 'secondary') return 'default';
  return 'primary';
};

export const CopyToClipboardButton = ({
  children,
  hasIcon = true,
  hasSuccessTooltip = false,
  textToCopy,
  variant = 'primary',
  tooltipText = 'Copy',
  tooltipSuccessText = 'Text copied!',
  disabled = false,
  icon,
  ...rest
}: Props & CommonHTMLButtonAttributes) => {
  const theme = useThemeValues();

  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ hoverDelay: 500 });
  const [copied, setCopied] = useState(false);
  const tooltipStyles = CopyToClipboardStyles.tooltip({ copied });
  const Icon = icon ?? CopyIcon;

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [tooltipProps.isOpen]);

  return (
    <>
      <CopyToClipboard
        text={textToCopy}
        onCopy={() => {
          if (hasSuccessTooltip) setCopied(true);
        }}
      >
        <Button
          variant={variant}
          disabled={disabled}
          css={css`
            align-items: center;
            display: flex;
            gap: ${theme.spacing(1)};
            padding: ${theme.spacing(1.5)};
            width: auto;
            .copy-icon {
              margin-left: 0;
            }
          `}
          {...triggerProps}
          {...rest}
        >
          {hasIcon && <Icon className='copy-icon' size={16} color={getIconColor(variant, children)} />}
          {children && (
            <Text as='span' color={getTextColor(variant)}>
              {children}
            </Text>
          )}
        </Button>
      </CopyToClipboard>
      {hasSuccessTooltip && !disabled && (
        <Tooltip {...tooltipProps} css={tooltipStyles}>
          {copied ? (
            <CheckIconSmall className='copy-status-icon' size={16} />
          ) : (
            <CopyIcon className='copy-status-icon' size={16} />
          )}
          <Text css={{ margin: 0 }} color='white'>
            {copied ? tooltipSuccessText : tooltipText}
          </Text>
        </Tooltip>
      )}
    </>
  );
};
